import React, { Component } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

// utils
import addDays from 'date-fns/add_days';
import detectMobileOS from 'utils/detectMobileOS';

import {
  Link,
  Status,
  DatePicker,
  Button,
  I18n,
  CommonErrorPopup,
  Preloader,
  Options,
} from 'components';

import { PopupDisposable } from '@beef/legacy-ui-kit';

import { formatDateString, formatCurrency } from 'utils/formatString';
import { getIsoDate } from 'utils/dates';
import declint from 'utils/declint';

import { gaYandexSubscriptionDisconnected } from 'utils/analytics/yandexSubscription';
import { pushServiceDisconnected } from 'utils/analytics/services';
import {
  yandexPlusConnectedYMUpd,
  yandexPlusDisconnectedSuccessYMUpd,
} from 'utils/analytics/yandexMetrikaYaSubscription';
import AbstractService, { cx, Description } from '../AbstractService';

const ERROR_DATA = {
  response: null,
  error: <I18n path="services.errorMessage" />,
  isLoading: true,
};

const getErrorData = ({
  code,
  service,
  message,
}) => {
  if (code === -1 || message) {
    return {
      response: null,
      error: message
        ? <div dangerouslySetInnerHTML={{ __html: message }} />
        : <I18n path="services.serviceDisconnectMessage" vars={{ service }} />,
      isLoading: true,
    };
  }

  return ERROR_DATA;
};

const formatDate = (input) => formatDateString(input, 'DD.MM.YYYY');

const StatusMessage = ({
  path,
  vars,
}) => (
  <div className={cx('response')}>
    <I18n
      path={path}
      vars={vars}
    />
  </div>
);
StatusMessage.propTypes = {
  path: PropTypes.string,
  vars: PropTypes.object,
};

const YANDEX_TARIFF_SUBSCRIPTION = 'YandexTariffSubscription';

export default class Service extends Component {
  constructor(props) {
    super(props);

    this.state = {
      disconnectionDate: new Date(),
      isDisconnectionFormOpened: false,
      isDisconnecting: false,
      response: null,
      error: false,
      isLoading: false,
      hideNativeView: false,
      isPopupOpened: false,
      disconnectPartnershipTariffStatus: 'initial',
      disconnectScreenProtection: 'initial',
      customServiceStatus: 'initial',
      popupInfo: {},
      isPartnershipTariff: false,
    };
  }

  componentDidMount() {
    const { type } = this.props;

    this.setState({
      hideNativeView: detectMobileOS() === 'iOS',
      isPartnershipTariff: type === YANDEX_TARIFF_SUBSCRIPTION,
    });
  }

  getPopupInfo = async (url) => {
    try {
      const res = await axios
        .get(url);
      this.setState({ popupInfo: res.data });
      this.setState((prevState) => ({
        isPopupOpened: !prevState.isPopupOpened,
      }));
    } catch (e) {
      console.error(e);
    }
  };

  toggleDisconnectionForm = (event) => {
    const { checked } = event.currentTarget;
    const { isPartnershipTariff } = this.state;
    const { title } = this.props;
    const serviceForYM = this.getPartnerOrBeelineService(isPartnershipTariff);

    if (this.props.type === YANDEX_TARIFF_SUBSCRIPTION) {
      if (checked) {
        /** Yandex Metrika Успешное подключение подписки Яндекс Плюс  */
        yandexPlusConnectedYMUpd(serviceForYM, title);
      } else {
        /** Yandex Metrika Успешное отключение подписки Яндекс Плюс  */
        yandexPlusDisconnectedSuccessYMUpd(serviceForYM, title);
      }
      this.getPopupInfo('/api/profile/userinfo/deactivate-YandexTariffSubscription-popup');
    } else if (this.props.type === 'Insurance') {
      this.getPopupInfo(`/api/alfa-insurance/deactivation-info?soc=${this.props.soc.toString()}`);
    } else {
      this.setState((prevState) => ({
        isDisconnectionFormOpened: !prevState.isDisconnectionFormOpened,
      }));
    }
  };

  handleDateChange = (value) => {
    this.setState({ disconnectionDate: value });
  };

  disconnectCustomService = (url, state, params) => {
    const handleDisconnectCustomService = async () => {
      try {
        const res = await axios.post(url, params);
        this.setState({
          customServiceStatus: res?.data.isSucceeded ? 'success' : 'error',
          [state]: res?.data.isSucceeded ? 'success' : 'error',
        });
      } catch (e) {
        console.error(e);
        this.setState({ [state]: 'error' });
      }
    };
    handleDisconnectCustomService();
    this.setState({
      customServiceStatus: 'pending',
      [state]: 'pending',
    });
  };

  getIconUrl = (state) => {
    if (state === 'success') {
      return this.state.popupInfo?.successPopup?.logoUrl;
    }
  };

  getPartnershipPopupHeading = (state) => {
    if (state === 'success') {
      return this.state.popupInfo?.successPopup.title;
    }
    return this.state.popupInfo?.confirmationPopup.title;
  };

  getPartnershipPopupDescription = (state) => {
    if (state === 'success') {
      return this.state.popupInfo?.successPopup.description;
    }
    return this.state.popupInfo?.confirmationPopup.description;
  };

  getButtonText = (state) => {
    if (state === 'success') {
      return this.state.popupInfo?.successPopup.buttonText;
    }
    return this.state.popupInfo?.confirmationPopup.buttonText;
  };

  handleDisconnect = (state) => {
    const { isPartnershipTariff } = this.state;
    const { title } = this.props;
    const serviceForYM = this.getPartnerOrBeelineService(isPartnershipTariff);

    if (state === 'error') {
      this.setState({
        isLoading: false,
        isPopupOpened: false,
      });
    } else if (state === 'success') {
      /** GA и Yandex Metrika Успешное отключение подписки Яндекс Плюс */
      if (this.props.type === 'YandexTariffSubscription') {
        gaYandexSubscriptionDisconnected();
        yandexPlusDisconnectedSuccessYMUpd(serviceForYM, title);
      }
      this.setState({
        isLoading: true,
        isPopupOpened: false,
      });
    }
  };

  disconnectService = () => {
    const {
      soc,
      title,
      actions,
    } = this.props;
    this.setState({
      isDisconnecting: true,
      error: null,
      isLoading: false,
    });

    actions.disconnectService(getIsoDate(this.state.disconnectionDate), soc[0], title)
      .then(
        (data) => (data.isSucceeded && (data?.result !== -1) && (data?.status !== 'semisuccess')
          ? {
            response: data,
            error: null,
          }
          : getErrorData({
            code: data.result,
            service: title,
            message: data?.status === 'semisuccess' && data.message,
          })),
        // Setting here `React.Component` as error will let us
        // to implement custom server message easily in future
        () => (ERROR_DATA),
      )
      .then(({
        response,
        error,
        isLoading,
      }) => {
        this.setState({
          response,
          error,
          isLoading,
          isDisconnecting: false,
        });
        pushServiceDisconnected(title);
      });
  };

  getOnClick = () => {
    if (this.props.type === 'YandexTariffSubscription'
      && this.state.disconnectPartnershipTariffStatus === 'initial') {
      this.disconnectCustomService(
        '/api/profile/userinfo/deactivateservice/',
        'disconnectPartnershipTariffStatus',
        {
          soc: this.props.partnershipTariffInfo.soc,
        },
      );
      this.setState({
        disconnectPartnershipTariffStatus: 'success',
        customServiceStatus: 'success',
      });
    } else {
      this.handleDisconnect(this.state.disconnectPartnershipTariffStatus);
    }

    if (this.props.type === 'Insurance'
      && this.state.disconnectScreenProtection === 'initial') {
      this.disconnectCustomService(
        '/api/alfa-insurance/deactivate/',
        'disconnectScreenProtection',
        {
          insuranceType: this.state.popupInfo.insuranceType,
          contractNumber: this.state.popupInfo.contractNumber,
        },
      );
    } else {
      this.handleDisconnect(this.state.disconnectScreenProtection);
    }
  };

  getPartnerOrBeelineService = (isPartnershipTariff) => (isPartnershipTariff ? 'partner' : 'beeline')

  render() {
    const {
      title,
      description,
      url,
      settingsUrl,
      isRemovable,
      isParametrable,
      rate,
      ratePeriod,
      rateDate,
      rateAfterChange,
      changRateDate,
      currency,
      dateConnect,
      dateDisconnect,
      isRestrictedAccess,
      tve,
      partnershipTariffInfo,
      type,
      productParameters,
      warningText,
      errorText,
      infoText,
      isInsurance,
    } = this.props;

    const {
      disconnectionDate,
      isDisconnectionFormOpened,
      isDisconnecting,
      response,
      error,
      isLoading,
      hideNativeView,
      isPopupOpened,
      customServiceStatus,
      isPartnershipTariff,
    } = this.state;

    const isVolte = title === 'VoLTE';
    const isCustomService = type === 'YandexTariffSubscription' || type === 'Insurance';
    const isFutureConnect = (dateConnect && Date.now() < dateConnect);
    const isFutureDisconnect = (isLoading && response && dateDisconnect && Date.now() < dateDisconnect);
    const isOpened = (isDisconnectionFormOpened && !response);
    const isSwitchDisabled = (isRestrictedAccess || !isRemovable || Boolean(response)
      || isDisconnecting || isLoading || isVolte);
    const isButtonShown = (isDisconnectionFormOpened && !response && !error && !isPartnershipTariff);
    const titleElement = url ? (
      <Link href={url} target="_blank">
        {title}
      </Link>
    ) : title;
    const initialStatus = customServiceStatus === 'initial';
    const successStatus = customServiceStatus === 'success';
    const pendingStatus = customServiceStatus === 'pending';

    let isSwitchChecked;

    if (isCustomService && (pendingStatus || successStatus)) {
      isSwitchChecked = false;
    } else if (isFutureDisconnect) {
      isSwitchChecked = response ? true : isOpened;
    } else {
      isSwitchChecked = response ? false : !isOpened;
    }

    const maxDisconnectionDate = addDays(new Date().getTime(), 30);

    return (
      <AbstractService
        checked={isSwitchChecked}
        disabled={isSwitchDisabled}
        onChange={this.toggleDisconnectionForm}
        title={titleElement}
        rate={rate}
        ratePeriod={ratePeriod}
        rateDate={rateDate}
        currency={currency}
        isFuture={isFutureConnect || isFutureDisconnect}
        isLoading={isLoading || Boolean(response)}
        showRateDate={!(tve && tve.isSuspended)}
        isPartnershipTariff={isPartnershipTariff}
      >
        {!isRemovable && (
          <StatusMessage
            path={!isInsurance
              ? 'services.statuses.unsubscribableService'
              : 'services.statuses.unsubscribableInsuranceService'}
          />
        )}
        {Date.now() < dateDisconnect && (
          <StatusMessage
            path="services.statuses.activeUntil"
            vars={{ date: formatDate(dateDisconnect) }}
          />
        )}
        {isFutureConnect && (
          <StatusMessage
            path="services.statuses.activeFrom"
            vars={{ date: formatDate(dateConnect) }}
          />
        )}
        {response && response.isSucceeded && (
          <StatusMessage
            path="services.servicesDisconnectMessage"
            vars={{ id: response.result }}
          />
        )}
        <Description html={description} />
        {!!productParameters?.length && (
          <Options productParameters={productParameters} />
        )}
        {(warningText || errorText || infoText) && (
          <Status
            text={warningText || errorText || infoText}
            className={['compact', { error: errorText, attention: warningText }, cx('info-messages')]}
          />
        )}
        {changRateDate && rateAfterChange && (
          <Status emoji="pointingHandUp" className={['compact', 'attention', cx('futureRateStatus')]}>
            C
            {' '}
            {formatDate(changRateDate)}
            {' '}
            абонентская плата
            {' '}
            составит
            {' '}
            {rateAfterChange}
            {' '}
            {formatCurrency(currency)}
            {' '}
            {ratePeriod}
          </Status>
        )}
        {tve && tve.isSuspended && (
          <Status emoji="pointingHandUp" className={['compact', 'attention', cx('tveStatus')]}>
            Подписки заблокированы, для просмотра деталей зайдите в настройки в приложении билайн ТВ
          </Status>
        )}
        {tve && (
          <div className={cx('tveMessage')}>
            {tve.deviceCount > 0
              ? declint(tve.deviceCount, [
                'Вы подключили %s устройство',
                'Вы подключили %s устройства',
                'Вы подключили %s устройств',
              ])
              : <I18n path="services.tveEmptyMessage" />}
          </div>
        )}
        {isParametrable && (
          <div className={cx('settingsLink')}>
            <Link
              href={settingsUrl || url}
              target="_blank"
            >
              <I18n path={isInsurance ? 'services.settingsInsuranceLink' : 'services.settingsLink'} />
            </Link>
          </div>
        )}
        {
          isPartnershipTariff
            && (partnershipTariffInfo.bonus || partnershipTariffInfo.bonus === 0)
            && partnershipTariffInfo.account && (
            <>
              <div className={cx('partnership-tariff-account')}>
                <div className={cx('partnership-tariff-account__text')}>
                  Аккаунт
                </div>
                <div className={cx('partnership-tariff-account__mail')}>
                  {partnershipTariffInfo.account}
                </div>
              </div>
              <div className={cx('partnership-tariff-account')}>
                <div className={cx('partnership-tariff-account__text')}>
                  Ваши баллы
                </div>
                <div className={cx('partnership-tariff-account__bonuses')}>
                  <img
                    src="https://static.beeline.ru/upload/images/b2c/yandex-tarif/plus-icon.svg"
                    alt="ya-bonus"
                    className={cx('partnership-tariff__account-icon')}
                  />
                  {partnershipTariffInfo.bonus}
                </div>
              </div>
            </>
          )
        }
        {isButtonShown && (
          <Status className={cx('disconnectionForm')}>
            <div className={cx('disconnectionFormTitle')}>
              Дата отключения
            </div>
            <div className={cx('disconnectionFormDatePicker')}>
              <DatePicker
                value={disconnectionDate}
                min={new Date()}
                max={maxDisconnectionDate}
                onChange={this.handleDateChange}
                hideNativeView={hideNativeView}
              />
            </div>
            <Button
              className="default"
              onClick={this.disconnectService}
              disabled={!disconnectionDate}
              isLoading={isDisconnecting}
            >
              Подтвердить
            </Button>
            <Link
              className={cx('disconnectionLink')}
              onClick={this.toggleDisconnectionForm}
            >
              Отменить
            </Link>
          </Status>
        )}
        {!!error && (
          <Status className={['error', cx('status')]}>
            {error}
          </Status>
        )}
        {isCustomService && isPopupOpened && customServiceStatus !== 'error'
        && (
          <PopupDisposable onClose={() => this.setState({ isPopupOpened: false })}>
            {successStatus && isPartnershipTariff && (
              <img
                className={cx('disconnection-popup__image')}
                src={this.getIconUrl(customServiceStatus)}
                alt="ya-success"
              />
            )}
            <h2 className={cx('disconnection-popup__heading')}>
              {this.getPartnershipPopupHeading(customServiceStatus)}
            </h2>
            {pendingStatus ? (
              <Preloader size="40" className={cx('preloader')} />
            ) : (
              <>
                <div
                  className={cx('disconnection-popup__description')}
                  dangerouslySetInnerHTML={{ __html: this.getPartnershipPopupDescription(customServiceStatus) }}
                />
                {
                  (initialStatus || successStatus) && (
                    <button className={cx('disconnection-popup__button')} onClick={this.getOnClick} type="button">
                      {this.getButtonText(customServiceStatus)}
                    </button>
                  )
                }
              </>
            )}
          </PopupDisposable>
        )}
        {customServiceStatus === 'error' && (
          <CommonErrorPopup onClose={this.getOnClick} />
        )}
      </AbstractService>
    );
  }
}

Service.propTypes = {
  soc: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  title: PropTypes.string,
  description: PropTypes.string,
  url: PropTypes.string,
  settingsUrl: PropTypes.string,
  rate: PropTypes.number,
  ratePeriod: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  rateDate: PropTypes.string,
  rateAfterChange: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  changRateDate: PropTypes.string,
  currency: PropTypes.string,
  isRemovable: PropTypes.bool,
  isParametrable: PropTypes.bool,
  actions: PropTypes.object,
  dateConnect: PropTypes.objectOf(Date),
  dateDisconnect: PropTypes.objectOf(Date),
  isRestrictedAccess: PropTypes.bool,
  tve: PropTypes.object,
  type: PropTypes.string,
  partnershipTariffInfo: PropTypes.objectOf(PropTypes.shape({
    bonus: PropTypes.number,
    account: PropTypes.string,
    soc: PropTypes.string,
  })),
  isInsurance: PropTypes.bool,
};
